import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag, Skull, Instagram, Youtube, Send } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api, formatError } from "../api";
import { toast, Toaster } from "sonner";
import { useHeaderLogo, useMarqueeMessages } from "./PageContent";

const NAV = [
  { to: "/", label: "The Dog House", testid: "nav-home" },
  { to: "/stories", label: "Stories", testid: "nav-stories" },
  { to: "/evidence", label: "Evidence", testid: "nav-evidence" },
  { to: "/crew", label: "The Crew", testid: "nav-crew" },
  { to: "/bad-ideas", label: "Bad Ideas", testid: "nav-bad-ideas" },
  { to: "/shop", label: "Spend Some Money", testid: "nav-shop" },
  { to: "/what-the-hell", label: "What The Hell Is Chibah?", testid: "nav-about" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const loc = useLocation();
  const { url: logoUrl, isCustom: customLogo } = useHeaderLogo();
  const marquee = useMarqueeMessages();

  return (
    <header className="border-b-4 border-asphalt sticky top-0 z-50 gritty-paper" data-testid="site-header">
      {/* Top marquee */}
      <div className="gritty-strip text-bone overflow-hidden border-b-2 border-asphalt">
        <div className="flex whitespace-nowrap marquee-track py-1.5 font-mono uppercase text-xs tracking-[0.25em] relative z-10">
          {["a", "b"].map((mk) => (
            <div key={mk} className="flex shrink-0 items-center gap-10 pr-10" data-testid={`marquee-track-${mk}`}>
              {marquee.map((msg, i) => (
                <span key={`${mk}-${i}-${msg}`}>✦ {msg}</span>
              ))}
              {/* Duplicate the list inside each track so the strip is dense enough to loop seamlessly */}
              {marquee.map((msg, i) => (
                <span key={`${mk}-d-${i}-${msg}`}>✦ {msg}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo">
          <img
            src={logoUrl}
            alt="Chibah Skateboards"
            className="h-14 sm:h-16 w-auto"
            style={customLogo ? undefined : { mixBlendMode: "multiply" }}
            data-testid="site-logo-img"
          />
        </Link>

        <nav className="hidden xl:flex items-center divide-x-2 divide-ink border-2 border-asphalt">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `px-4 py-3 font-mono uppercase text-[11px] tracking-[0.18em] transition-colors ${
                  isActive ? "bg-asphalt text-bone" : "hover:bg-asphalt hover:text-bone"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/complain"
            data-testid="header-complain-btn"
            className="hidden md:inline-flex items-center gap-2 border-2 border-asphalt px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] shadow-hard hover:bg-asphalt hover:text-bone transition-all"
          >
            <Send className="w-3.5 h-3.5" /> Complain Here
          </Link>
          <Link
            to="/cart"
            data-testid="header-cart-btn"
            className="relative inline-flex items-center gap-2 bg-terra border-2 border-asphalt px-4 py-2 text-bone font-mono text-[11px] uppercase tracking-[0.2em] shadow-hard hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_#1C1B1A] transition-all"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Cart</span>
            <span className="bg-bone text-asphalt px-1.5 -ml-1 text-[10px]" data-testid="cart-count">
              {count}
            </span>
          </Link>
          <button
            className="xl:hidden border-2 border-asphalt p-2 shadow-hard"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t-2 border-asphalt bg-bone">
          <div className="flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${n.testid}`}
                className={({ isActive }) =>
                  `px-5 py-4 font-mono uppercase text-xs tracking-[0.2em] border-b border-ink ${
                    isActive ? "bg-asphalt text-bone" : ""
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/complain"
              onClick={() => setOpen(false)}
              data-testid="mobile-complain-btn"
              className="px-5 py-4 font-mono uppercase text-xs tracking-[0.2em] bg-terra text-bone"
            >
              Complain Here →
            </Link>
          </div>
        </div>
      )}

      <div className="tape-divider-gritty" />
      <Toaster position="top-center" toastOptions={{ style: { fontFamily: "Courier Prime, monospace", borderRadius: 0 } }} />
      {loc.pathname === "/x" && null}
    </header>
  );
};

export const NewsletterBlock = ({ compact = false }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      toast.success(data.message || "Trouble incoming.");
      setEmail("");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Could not subscribe.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="newsletter-form"
      className={`${
        compact ? "" : "border-2 border-bone p-6"
      } flex flex-col sm:flex-row gap-3 items-stretch`}
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email address, dispatcher."
        data-testid="newsletter-email-input"
        className="flex-1 bg-bone text-asphalt px-4 py-3 border-2 border-bone font-mono text-sm focus:outline-none focus:border-terra"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="newsletter-submit-btn"
        className="bg-terra text-bone border-2 border-bone px-6 py-3 font-display tracking-wider uppercase text-lg shadow-hard-bone hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_#F5F2E9] transition-all disabled:opacity-60"
      >
        {loading ? "Saddling up..." : "Trouble Ahead →"}
      </button>
    </form>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-asphalt text-bone mt-20" data-testid="site-footer">
      <div className="tape-divider" />
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="font-western text-3xl mb-2">Trouble Ahead.</div>
          <p className="font-body text-sm text-bone/80 max-w-md mb-6">
            Letters from the dog house. Stories, evidence, and bad ideas delivered straight to your inbox at roughly the
            speed of a postal mule.
          </p>
          <NewsletterBlock />
        </div>
        <div className="lg:col-span-3">
          <div className="font-mono uppercase text-xs tracking-[0.25em] text-bone/60 mb-4">The Dog House</div>
          <ul className="space-y-2 font-body">
            <li><Link className="hover:text-terra" to="/stories">Stories</Link></li>
            <li><Link className="hover:text-terra" to="/evidence">Evidence</Link></li>
            <li><Link className="hover:text-terra" to="/crew">The Crew</Link></li>
            <li><Link className="hover:text-terra" to="/bad-ideas">Bad Ideas</Link></li>
            <li><Link className="hover:text-terra" to="/shop">Spend Some Money</Link></li>
            <li><Link className="hover:text-terra" to="/what-the-hell">What The Hell Is Chibah?</Link></li>
          </ul>
        </div>
        <div className="lg:col-span-4">
          <div className="font-mono uppercase text-xs tracking-[0.25em] text-bone/60 mb-4">Find us in the wild</div>
          <div className="flex gap-3 mb-6">
            <a href="https://instagram.com" target="_blank" rel="noreferrer" data-testid="social-instagram"
              className="border-2 border-bone p-3 hover:bg-terra hover:border-terra transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="https://youtube.com" target="_blank" rel="noreferrer" data-testid="social-youtube"
              className="border-2 border-bone p-3 hover:bg-terra hover:border-terra transition-colors">
              <Youtube className="w-5 h-5" />
            </a>
          </div>
          <Link
            to="/complain"
            data-testid="footer-complain-btn"
            className="inline-flex items-center gap-2 bg-bone text-asphalt border-2 border-bone px-5 py-3 font-display tracking-wider uppercase text-lg shadow-[4px_4px_0_#C84C32] hover:bg-terra hover:text-bone transition-colors"
          >
            <Send className="w-4 h-4" /> Complain Here
          </Link>
        </div>
      </div>
      <div className="border-t-2 border-bone/30 py-5 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-bone/70">
        © {new Date().getFullYear()} Chibah Skateboards · Made under a desert sun
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => (
  <div className="min-h-screen bg-bone text-asphalt flex flex-col">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);
